import { useEffect, useState, useMemo } from 'react';
import { geoMercator, geoPath, type GeoPermissibleObjects } from 'd3-geo';
import styles from './BrazilMapSVG.module.css';
import {
	loadBrazilGeoJSON,
	getCachedBrazilGeoJSON,
	type BrazilGeoJSON,
} from '../../../utils/brazilGeoJSON';

const CANONICAL_W = 120;
const CANONICAL_H = 90;

interface BrazilMapSVGProps {
	selectedStateCodes: string[];
	className?: string;
}

export const BrazilMapSVG = ({ selectedStateCodes, className }: BrazilMapSVGProps) => {
	const [geoJson, setGeoJson] = useState<BrazilGeoJSON | null>(getCachedBrazilGeoJSON());

	useEffect(() => {
		if (getCachedBrazilGeoJSON()) return;
		loadBrazilGeoJSON()
			.then((data) => setGeoJson(data))
			.catch(() => {});
	}, []);

	const statePaths = useMemo(() => {
		if (!geoJson) return [];
		const projection = geoMercator().fitSize([CANONICAL_W, CANONICAL_H], geoJson as unknown as GeoPermissibleObjects);
		const pathGen = geoPath(projection);
		return geoJson.features.map((feature) => ({
			d: pathGen(feature as unknown as GeoPermissibleObjects),
			sigla: (feature.properties?.sigla as string) ?? '',
		}));
	}, [geoJson]);

	if (!geoJson) {
		return <div className={`${styles.placeholder} ${className ?? ''}`} />;
	}

	return (
		<svg
			viewBox={`0 0 ${CANONICAL_W} ${CANONICAL_H}`}
			className={`${styles.svg} ${className ?? ''}`}
			preserveAspectRatio="xMidYMid meet"
		>
			{statePaths.map(({ d, sigla }) =>
				d ? (
					<path
						key={sigla || d.slice(0, 8)}
						d={d}
						className={`${styles.statePath} ${selectedStateCodes.includes(sigla) ? styles.stateSelected : styles.stateBase}`}
					/>
				) : null
			)}
		</svg>
	);
};
